import { appConfig } from '@/config/app.config';
import { getLogger } from '@/lib/logger';
import { I18nextPhraseBackend } from '@phrase/i18next-backend';
import i18n, { type InitOptions } from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { buildPhraseApiLoadPath, getPhraseApiHeaders } from './i18n.utils';
import { normalizeLanguage, SUPPORTED_LANGUAGES, type Language } from './languages';
import deLocale from './locales/de';
import enLocale from './locales/en';

const logger = getLogger({ scope: 'lib.i18n' });

export const LANGUAGE_STORAGE_KEY = 'app.language';

export function getInitialLanguage(): Language {
  try {
    const stored = normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
    if (stored) {
      return stored;
    }
  } catch {
    // localStorage unavailable
  }
  // No stored preference yet: follow the browser language on first visit.
  return normalizeLanguage(navigator.language) ?? SUPPORTED_LANGUAGES.EN;
}

const SHARED_OPTIONS: InitOptions = {
  load: 'languageOnly',
  // Constrain resolution to the languages we ship so i18n.resolvedLanguage is
  // always a supported code (e.g. "de-DE" resolves to "de").
  supportedLngs: Object.values(SUPPORTED_LANGUAGES),
  lng: getInitialLanguage(),
  fallbackLng: SUPPORTED_LANGUAGES.EN,
  partialBundledLanguages: true,
  interpolation: { escapeValue: false },
};

if (appConfig.phrase.api) {
  void i18n
    .use(HttpApi)
    .use(initReactI18next)
    .init({
      ...SHARED_OPTIONS,
      backend: {
        loadPath: (lng: string[]) => buildPhraseApiLoadPath(lng[0]),
        customHeaders: getPhraseApiHeaders(),
      },
    })
    .catch((error) => logger.error('Failed to initialise i18n', { error }));
} else {
  void i18n
    .use(I18nextPhraseBackend)
    .use(initReactI18next)
    .init({
      ...SHARED_OPTIONS,
      backend: {
        host: appConfig.phrase.host,
        distribution: appConfig.phrase.distribution,
        environment: appConfig.phrase.secret,
        appVersion: appConfig.phrase.appVersion,
        cacheExpirationTime: appConfig.phrase.cacheExpirationTimeMs / 1000,
      },
    })
    .catch((error) => logger.error('Failed to initialise i18n', { error }));
}

// Register bundled namespaces after init so the Phrase OTA backend is not disabled.
// See: https://www.i18next.com/how-to/add-or-load-translations#add-after-init
// deep=true merges with existing keys; overwrite=false prevents overriding Phrase keys.

for (const [namespace, translations] of Object.entries(enLocale)) {
  i18n.addResourceBundle(SUPPORTED_LANGUAGES.EN, namespace, translations, true, false);
}
for (const [namespace, translations] of Object.entries(deLocale)) {
  i18n.addResourceBundle(SUPPORTED_LANGUAGES.DE, namespace, translations, true, false);
}

i18n.on('languageChanged', (lang: string) => {
  const languageOnly = normalizeLanguage(lang);
  if (!languageOnly) {
    return;
  }
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, languageOnly);
  } catch {
    // localStorage unavailable
  }
});

export default i18n;
